import { Table,Tag } from "antd";
import {
  checkInQRTokenApi,
  getJobDetailApi,
  getJobDriveInvitationsApi,
  sendJobDriveInvitationsApi
} from "api/services/jobs";
import QRScanner from "components/Administration/Jobs/JobDrive/QRScanner";
import Breadcrumbs from "components/Breadcrumbs";
import FilterInput from "components/Form/filterInput";
import { SkeltonLoader } from "components/skeltonLoader";
import { format,parseISO } from "date-fns";
import { useEffect,useMemo,useState } from "react";
import { useSelector } from "react-redux";
import { Link,Navigate,useParams,useSearchParams } from "react-router-dom";
import { Bounce,toast } from "react-toastify";
import {
  itemRender,
  pageSizeOptions,
} from "utils/table";

const JobDriveDashboard = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = id || searchParams.get("job");
  const { user, userLoading } = useSelector((state) => state.user);
  
  // Check if user has job_drive permission
  const hasJobDrivePermission = useMemo(() => {
    if (!user?.role?.permissions) return false;
    return user.role.permissions.some((p) => p.code === "job_drive") ||
           user?.modules?.some?.((m) => m.code === "job_drive");
  }, [user]);

  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [jobDetail, setJobDetail] = useState(null);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSettings, setPageSettings] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
  });

  const fetchJobDetail = async () => {
    try {
      const response = await getJobDetailApi(jobId, {});
      setJobDetail(response.data);
    } catch (error) {
      console.error("Error fetching job detail:", error);
    }
  };

  const fetchInvitations = async (page = 1, pageSize = 50, search = "") => {
    setLoading(true);
    try {
      const params = { 
        job_id: jobId,
        page,
        size: pageSize
      };
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await getJobDriveInvitationsApi(params);
      // Handle different response structures
      const invitationsData = response.data;
      let invitationsArray = [];
      if (Array.isArray(invitationsData)) {
        invitationsArray = invitationsData;
      } else if (invitationsData?.results && Array.isArray(invitationsData.results)) {
        invitationsArray = invitationsData.results;
      } else if (invitationsData?.invitations && Array.isArray(invitationsData.invitations)) {
        invitationsArray = invitationsData.invitations;
      }
      setInvitations(invitationsArray);
      
      // Update pagination
      if (invitationsData?.count !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: invitationsData.count,
          page,
          pageSize
        }));
      } else if (invitationsData?.total !== undefined) {
        setPageSettings(prev => ({
          ...prev,
          total: invitationsData.total,
          page,
          pageSize
        }));
      }
    } catch (error) {
      console.error("Error fetching invitations:", error);
      setInvitations([]); // Ensure invitations is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    fetchInvitations(pagination.current, pagination.pageSize, searchTerm);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  useEffect(() => {
    if (jobId) {
      fetchJobDetail();
      fetchInvitations(pageSettings.page, pageSettings.pageSize, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Debounced search effect
  useEffect(() => {
    if (jobId) {
      const timeoutId = setTimeout(() => {
        setPageSettings(prev => ({
          ...prev,
          page: 1
        }));
        fetchInvitations(1, pageSettings.pageSize, searchTerm);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Handle pagination changes (but not when search term changes, as that's handled above)
  useEffect(() => {
    if (jobId && searchTerm === "") {
      fetchInvitations(pageSettings.page, pageSettings.pageSize, searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSettings.page, pageSettings.pageSize]);

  // Wait for user to load before checking permissions
  if (userLoading) {
    return <div>Loading...</div>;
  }

  // Redirect if no permission (after hooks)
  if (!hasJobDrivePermission) {
    return <Navigate to="/manage-jobs" replace />;
  }

  const handleSendInvitation = async (applicantId, slotId) => {
    try {
      await sendJobDriveInvitationsApi([applicantId], slotId);
      
      // Update local state to reflect the invitation was sent
      setInvitations(prevInvitations => 
        prevInvitations.map(invitation => {
          if (invitation.applicant?.id === applicantId && invitation.slot?.id === slotId) {
            return {
              ...invitation,
              status: "sent"
            };
          }
          return invitation;
        })
      );
      
      toast.success("Invitation sent successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Failed to send invitation";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleScanSuccess = async (scanData) => {
    if (scanData?.token) {
      try {
        await checkInQRTokenApi(scanData.token);
        toast.success("Check-in successful!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchInvitations(pageSettings.page, pageSettings.pageSize, searchTerm);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to check in", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    }
  };

  const invitationColumns = [
    {
      title: "Name",
      render: (_, record) => {
        const applicant = record.applicant;
        return `${applicant?.first_name || ""} ${applicant?.last_name || ""}`.trim() || "-";
      },
    },
    {
      title: "Email",
      render: (_, record) => record.applicant?.email || record.email_to || "-",
    },
    {
      title: "Phone",
      render: (_, record) => record.applicant?.phone || record.applicant?.mobile || "-",
    },
    {
      title: "Time Slot",
      render: (_, record) => {
        if (record.slot) {
          const startDate = parseISO(record.slot.start_at);
          const endDate = parseISO(record.slot.end_at);
          return (
            <div>
              <div>{record.slot.title || "-"}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {format(startDate, "dd-MM-yyyy HH:mm")} - {format(endDate, "HH:mm")}
              </div>
              {record.slot.location && (
                <div style={{ fontSize: "11px", color: "#999" }}>
                  {record.slot.location}
                </div>
              )}
            </div>
          );
        }
        return <Tag color="default">Not Assigned</Tag>;
      },
    },
    // {
    //   title: "Invitation Status",
    //   render: (_, record) => {
    //     if (record.status === "sent") {
    //       return <Tag color="success">Sent</Tag>;
    //     }
    //     return <Tag color="default">Pending</Tag>;
    //   },
    // },
    {
      title: "Checked In",
      render: (_, record) => {
        if (record.checked_in_at) {
          return (
            <div>
              <Tag color="success">Yes</Tag>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>
                {format(parseISO(record.checked_in_at), "dd-MM-yyyy HH:mm")}
              </div>
            </div>
          );
        }
        return <Tag color="default">No</Tag>;
      },
    },
    {
      title: "Action",
      render: (_, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            {record.slot && record.status !== "sent" && (
              <Link
                to="#"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  handleSendInvitation(record.applicant?.id, record.slot.id);
                }}
              >
                <i className="fa fa-paper-plane m-r-5" /> Send Invitation
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={jobDetail ? `Job Drive - ${jobDetail.title}` : "Job Drive Dashboard"}
          title="Dashboard"
          subtitle="Job Drive"
          name="Scan Code"
          setJobModal={() => setQrScannerOpen(true)}
          hideGrid={true}
        />
        
        <div className="row mb-4">
          <div className="col-lg-4 col-md-12">
            <FilterInput  
              defaultValue={searchTerm} 
              onChange={handleSearchChange} 
              id="search"
              label="Search by name, email, or phone"
              // placeholder="Search by name, email, or phone"
            />
          </div>
        </div>
        
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", overflow: "hidden" }}>
              <style>{`
                .modern-table .ant-table-tbody > tr {
                  background-color: #ffffff !important;
                }
                .modern-table .ant-table-tbody > tr:hover > td {
                  background-color: #f5f5f5 !important;
                }
                .modern-table .ant-table-thead > tr > th {
                  background-color: #f8f9fa !important;
                  font-weight: 600;
                  color: #333;
                  border-bottom: 2px solid #e0e0e0;
                  padding: 10px 12px;
                  font-size: 13px;
                }
                .modern-table .ant-table-tbody > tr > td {
                  border-bottom: 1px solid #f0f0f0;
                  padding: 10px 12px;
                  font-size: 13px;
                }
                .modern-table .ant-table-wrapper .ant-table-pagination.ant-pagination {
                  border-top: 1px solid #f0f0f0;
                  background-color: #ffffff;
                  border-bottom-left-radius: 12px;
                  border-bottom-right-radius: 12px;
                }
                .modern-table .ant-table-tbody > tr > td:last-child {
                  position: relative !important;
                  overflow: visible !important;
                }
                .modern-table .dropdown-action {
                  position: relative !important;
                }
                .modern-table .dropdown-menu {
                  z-index: 9999 !important;
                  position: absolute !important;
                  top: 100% !important;
                  right: 0 !important;
                  left: auto !important;
                  margin-top: 0.125rem !important;
                  min-width: 160px;
                }
                .modern-table .ant-table-wrapper,
                .modern-table .ant-table-body,
                .modern-table .ant-table-container,
                .modern-table .ant-table {
                  overflow: visible !important;
                }
              `}</style>
              {loading ? (
                <SkeltonLoader number={5} active={true} />
              ) : (
                <Table
                  className="modern-table"
                  columns={invitationColumns}
                  dataSource={Array.isArray(invitations) ? invitations : []}
                  rowKey="id"
                  pagination={{
                    total: pageSettings?.total,
                    pageSize: pageSettings?.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: pageSizeOptions,
                    itemRender: itemRender,
                    position: "right",
                    current: pageSettings?.page,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    style: { padding: "16px" }
                  }}
                  onChange={handleTableChange}
                />
              )}
            </div>
          </div>
        </div>

        {/* Modals */}
        <QRScanner
          open={qrScannerOpen}
          onCancel={() => setQrScannerOpen(false)}
          onScanSuccess={handleScanSuccess}
        />
      </div>
    </div>
  );
};

export default JobDriveDashboard;

